import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { AiTrackingOrigin } from '@databox/types/domains/ai/chat.enums';

import type { PopupData, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import type { Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

// Lists the space's goals and hands the pick back; goal/selector resolves a metric by goal key instead.
export default class HomePopupPickGoalComponent extends PopupInnerModalComponent<PopupData.Home.PickGoal> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	@tracked spaceGoals: LabelValuePairString[] = [];
	@tracked selectedGoal = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.refreshSpaceGoals();
	}

	get isSubmitDisabled(): boolean {
		return !this.selectedGoal;
	}

	refreshSpaceGoals(): void {
		this.spaceGoals = this.mmGoalsService.goalForSpaceModels.map((goal) => ({
			avatar: goal.sourceCM?.realAvatar,
			value: goal.id!,
			label: goal.title,
			label2: goal.source?.title,
		}));
	}

	@action
	createNewGoal(): void {
		this.mmGoalsService.openNewPopup(AiTrackingOrigin.HOME, (model) => {
			this.refreshSpaceGoals();
			this.changeGoal(model.id!);
		});
	}

	@action
	changeGoal(newGoalId: string): void {
		this.selectedGoal = newGoalId;
	}

	@action
	save(): void {
		if (!this.selectedGoal) {
			return;
		}

		this.popupData.onSubmit(Number(this.selectedGoal));
		this.close();
	}

	<template>
		<div class="title">
			<CommonUiIconComponent @name="Target" @size="lg" />Select goal
		</div>

		<div class="content">
			<h4>Select a goal you would like to add to Home:</h4>
			<div class="row">
				<FormSelectComponent
					name="goal"
					@placeholder="Select goal"
					@selected={{this.selectedGoal}}
					@options={{this.spaceGoals}}
					@isNumeric={{false}}
					@onChange={{this.changeGoal}}
					@maxOptionsShown={{this.spaceGoals.length}}
					@extraClass="goal-select"
				/>
			</div>

			<div class="row">
				<a role="button" class="link link--blue" {{on "click" this.createNewGoal}}>
					+ New goal
				</a>
			</div>

			<div class="controls2">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text="Add to Home"
					@extraClass="save"
					@onTrigger={{this.save}}
					@isDisabled={{this.isSubmitDisabled}}
				/>
			</div>
		</div>
	</template>
}
